<template>
  <div class="search-container overflow-x-auto">
    <div
      class="flex items-center gap-4 w-fit bg-secondary-50 p-2 rounded text-sm font-semibold"
    >
      <span
        v-for="(search, index) in nestedSearches || []"
        :key="index"
        class="test-sm"
      >
        <span
          class="px-3 py-2 rounded flex items-center cursor-pointer"
          :class="{
            'bg-secondary-700 text-white':
              search?.attributes.name === selectedNestedSearch.name,
            'hover:text-secondary-600 hover:bg-accent-200':
              search?.attributes.name !== selectedNestedSearch.name,
          }"
          @click="selectNewSearch(search)"
        >
          <span class="font-body-2">{{ search?.attributes.name }} </span>
          <font-awesome-icon
            v-if="
              search?.attributes.name === selectedNestedSearch.name &&
              !search?.attributes.is_primary_profile &&
              hasManySearches &&
              onSearchPage
            "
            class="ml-3"
            :icon="['far', 'xmark']"
            @click.stop="onRemoveSearch(search)"
          />
        </span>
      </span>

      <span v-if="onSearchPage" class="bg-secondary-600 h-5 w-[1px]"> </span>
      <el-tooltip
        :content="$t('search.errors.childLimitTooltip', totalCompetingProfiles)"
        :placement="'top'"
        :popper-class="'font-sans'"
        :show-after="500"
        :disabled="!searchPlanLimitReached"
      >
        <span
          v-if="onSearchPage"
          class="bg-white text-secondary-600 flex justify-center items-center w-8 h-8 rounded cursor-pointer hover:bg-secondary-100 border border-secondary-600"
          :class="`${searchPlanLimitReached || props.isUnsavedNestedSearch ? 'disabled' : ''}`"
          @click="createNestedSearch"
        >
          <font-awesome-icon :icon="['far', 'plus']" />
        </span>
      </el-tooltip>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { SavedSearchItem } from "~/types/SearchStateType";
import SavedSearchAttributes from "~/types/attributes/SavedSearchAttributesType";
import { useFeatureManagement } from "~/composables/useFeatureManagement";
const { getFeatureConfig } = useFeatureManagement();

const emit = defineEmits([
  "search-selected",
  "remove-search",
  "open-new-search",
]);

const props = defineProps({
  nestedSearches: {
    type: Array<SavedSearchItem>,
    required: true,
  },
  selectedNestedSearch: {
    type: Object,
    required: true,
  },
  isUnsavedNestedSearch: {
    type: Boolean,
    default: false,
  },
  orgDetails: {
    type: Object,
    default: () => {},
  },
  onSearchPage: {
    type: Boolean,
    default: false,
  },
});

const hasManySearches = computed(() => {
  return props.nestedSearches.length > 1;
});

const totalCompetingProfiles = computed(() => {
  const maxCompetitorProfiles = getFeatureConfig<number>(
    "searchProfiles.maxCompetitorProfilesPerComparison",
  );
  return maxCompetitorProfiles ? maxCompetitorProfiles + 1 : 0;
});

const searchPlanLimitReached = computed(() => {
  const maxCompetitorProfiles = getFeatureConfig<number>(
    "searchProfiles.maxCompetitorProfilesPerComparison",
  );
  if (!maxCompetitorProfiles) return true;

  return props.nestedSearches.length === totalCompetingProfiles.value;
});

const selectNewSearch = (search: SavedSearchAttributes) => {
  if (search.name !== props.selectedNestedSearch.name) {
    emit("search-selected", search);
  }
};
const onRemoveSearch = (search: SavedSearchAttributes) => {
  emit("remove-search", search);
};

const createNestedSearch = () => {
  if (searchPlanLimitReached.value || props.isUnsavedNestedSearch) {
    return;
  }
  emit("open-new-search");
};
</script>

<style>
.disabled {
  @apply text-white bg-accent-400 cursor-not-allowed hover:bg-accent-400;
}
</style>
